import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import MergeRolesDialog from "../components/MergeRolesDialog";
import {
  ArrowLeft, Plus, Pencil, Trash2, ChevronRight,
  CheckCircle2, Circle, AlertTriangle, Building2, Loader2, Briefcase, Merge,
  X, Wand2, ChevronLeft, Search, Sparkles,
} from "lucide-react";

const ROUNDS = ["hr", "technical", "behavioral"];
const CURRENT_BANK_PROMPT_VERSION = 3;

// Role origin classification by `source`. Premium = candidate self-service (JIT); Admin = created
// or approved by an admin; Seeded = bulk O*NET catalog / system. Anything unknown falls to Seeded.
const ROLE_ORIGINS = {
  admin:   { label: "Admin",        cls: "bg-[#1D4ED8]/10 text-[#1D4ED8] border-[#1D4ED8]/30",
             sources: ["admin", "admin_profile", "admin_merge", "admin_approved"] },
  premium: { label: "Premium user", cls: "bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/30",
             sources: ["auto"] },
  seeded:  { label: "Seeded",       cls: "bg-secondary text-muted-foreground border-border",
             sources: [] },
};
const originOf = (role) => {
  const s = role?.source;
  if (ROLE_ORIGINS.premium.sources.includes(s)) return "premium";
  if (ROLE_ORIGINS.admin.sources.includes(s)) return "admin";
  return "seeded";
};
const ORIGIN_FILTERS = [["all", "All"], ["admin", "Admin"], ["premium", "Premium user"], ["seeded", "Seeded"]];

export default function AdminRoles() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [allGaps, setAllGaps] = useState({});
  const [allBankAlerts, setAllBankAlerts] = useState({});

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [createIndustryId, setCreateIndustryId] = useState("");
  const [createFamilyId, setCreateFamilyId] = useState("");
  const [createTools, setCreateTools] = useState([]);
  const [createAutoGenerate, setCreateAutoGenerate] = useState(true);
  const [toolInput, setToolInput] = useState("");
  const [toolSuggestions, setToolSuggestions] = useState([]);
  const [toolsDiscovering, setToolsDiscovering] = useState(false);
  const toolInputRef = useRef(null);
  const toolDebounceRef = useRef(null);
  const toolsDiscoveredRef = useRef(false);  // auto-discover tools only once per dialog open

  // Role name autocomplete
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [nameLoading, setNameLoading] = useState(false);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const nameDebounceRef = useRef(null);
  const nameWrapperRef = useRef(null);

  // Edit dialog
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editIndustryId, setEditIndustryId] = useState("");
  const [editFamilyId, setEditFamilyId] = useState("");

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Merge dialog
  const [mergeOpen, setMergeOpen] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState("");

  // Origin filter (all | admin | premium | seeded)
  const [originFilter, setOriginFilter] = useState("all");

  // Premium-roles summary (banner + NEW badges); loaded once per visit, then marks page as seen.
  const [premiumSummary, setPremiumSummary] = useState(null);
  const premiumLoadedRef = useRef(false);

  useEffect(() => {
    const handler = (e) => {
      if (nameWrapperRef.current && !nameWrapperRef.current.contains(e.target)) {
        setShowNameSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchNameSuggestions = (query) => {
    clearTimeout(nameDebounceRef.current);
    if (query.trim().length < 2) {
      setNameSuggestions([]);
      setShowNameSuggestions(false);
      return;
    }
    setNameLoading(true);
    nameDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/roles/title-search", { params: { q: query.trim() } });
        setNameSuggestions(data || []);
        setShowNameSuggestions((data || []).length > 0);
      } catch {
        setNameSuggestions([]);
      } finally {
        setNameLoading(false);
      }
    }, 300);
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const [rolesRes, gapsRes, indRes, famRes, alertsRes] = await Promise.all([
        api.get("/admin/roles"),
        api.get("/admin/coverage-gaps").catch(() => ({ data: {} })),
        api.get("/admin/industries").catch(() => ({ data: [] })),
        api.get("/admin/families").catch(() => ({ data: [] })),
        api.get("/admin/bank-alerts").catch(() => ({ data: {} })),
      ]);
      setRoles(rolesRes.data);
      setAllGaps(gapsRes.data);
      setIndustries(indRes.data);
      setFamilies(famRes.data);
      setAllBankAlerts(alertsRes.data);
    } catch {
      toast.error("Could not load roles");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchRoles(); }, []);

  // Load the premium-roles summary once, then advance the "seen" pointer so the dashboard
  // indicator clears. Ref-guarded against React StrictMode double-invoke.
  useEffect(() => {
    if (premiumLoadedRef.current) return;
    premiumLoadedRef.current = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/premium-roles-summary");
        setPremiumSummary(data);
      } catch { /* non-fatal */ }
      api.post("/admin/roles/mark-seen").catch(() => {});
    })();
  }, []);

  const newRoleIds = new Set(premiumSummary?.new_role_ids || []);

  const resetCreate = () => {
    setCreateOpen(false);
    setCreateStep(1);
    setCreateName(""); setCreateDesc("");
    setCreateIndustryId(""); setCreateFamilyId("");
    setCreateTools([]); setToolsDiscovering(false); toolsDiscoveredRef.current = false;
    setCreateAutoGenerate(true);
    setToolInput(""); setToolSuggestions([]);
    setNameSuggestions([]); setShowNameSuggestions(false);
  };

  const fetchToolSuggestions = (q) => {
    clearTimeout(toolDebounceRef.current);
    if (!q.trim()) { setToolSuggestions([]); return; }
    toolDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/roles/tools-autocomplete", { params: { q: q.trim() } });
        setToolSuggestions((data || []).filter(t => !createTools.includes(t)));
      } catch { setToolSuggestions([]); }
    }, 200);
  };

  const addTool = (tool) => {
    const t = tool.trim();
    if (t && !createTools.includes(t)) setCreateTools(prev => [...prev, t]);
    setToolInput(""); setToolSuggestions([]);
    toolInputRef.current?.focus();
  };

  const removeTool = (tool) => setCreateTools(prev => prev.filter(t => t !== tool));

  // LLM-infer tools from the role name + industry/family. Auto-runs once on entering step 2;
  // `force` (the ↻ re-suggest button) replaces the current chips with a fresh suggestion set.
  const discoverTools = async ({ force = false } = {}) => {
    if (!createName.trim()) return;
    if (!force && toolsDiscoveredRef.current) return;
    toolsDiscoveredRef.current = true;
    setToolsDiscovering(true);
    try {
      const { data } = await api.post("/admin/roles/discover-tools", {
        name: createName.trim(),
        industry_id: createIndustryId || null,
        family_id: createFamilyId || null,
      });
      const found = data?.tools || [];
      setCreateTools(prev => {
        if (force) return found;
        const seen = new Set(prev.map(t => t.toLowerCase()));
        return [...prev, ...found.filter(t => !seen.has(t.toLowerCase()))];
      });
    } catch {
      // silent — admin can still type tools manually
    } finally {
      setToolsDiscovering(false);
    }
  };

  const performCreate = async () => {
    if (createStep === 1) {
      if (!createName.trim()) { toast.error("Name is required"); return; }
      setCreateStep(2);
      discoverTools();  // auto-fill tool chips on entering step 2 (once per dialog open)
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/roles", {
        name: createName.trim(),
        description: createDesc.trim(),
        industry_id: createIndustryId || null,
        family_id: createFamilyId || null,
        tools: createTools,
      });
      if (createAutoGenerate) {
        api.post(`/admin/roles/${data.role_id}/generate-from-profile`).catch(() => {});
        toast.success(`"${data.name}" created — generating questions in background`);
      } else {
        toast.success(`Role "${data.name}" created`);
      }
      resetCreate();
      navigate(`/admin/roles/${data.role_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create role");
    } finally { setBusy(false); }
  };

  const performEdit = async () => {
    if (!editName.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/roles/${editTarget.role_id}`, {
        name: editName.trim(),
        description: editDesc.trim(),
        industry_id: editIndustryId || null,
        family_id: editFamilyId || null,
      });
      toast.success("Role updated");
      setRoles(arr => arr.map(r => r.role_id === editTarget.role_id
        ? { ...r, name: editName.trim(), description: editDesc.trim(), industry_id: editIndustryId || null, family_id: editFamilyId || null }
        : r));
      setEditTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update role");
    } finally { setBusy(false); }
  };

  const toggleActive = async (role) => {
    try {
      await api.patch(`/admin/roles/${role.role_id}`, { is_active: !role.is_active });
      setRoles(arr => arr.map(r => r.role_id === role.role_id ? { ...r, is_active: !role.is_active } : r));
      toast.success(`${role.name} ${!role.is_active ? "activated" : "deactivated"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update role");
    }
  };

  const performDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/roles/${deleteTarget.role_id}`);
      toast.success(`"${deleteTarget.name}" deleted`);
      setRoles(arr => arr.filter(r => r.role_id !== deleteTarget.role_id));
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete role");
    } finally { setBusy(false); }
  };

  // Collapse state — empty Sets mean everything collapsed by default
  const [expandedIndustries, setExpandedIndustries] = useState(new Set());
  const [expandedFamilies, setExpandedFamilies] = useState(new Set());

  const toggleIndustry = (indId) => setExpandedIndustries((prev) => {
    const next = new Set(prev);
    next.has(indId) ? next.delete(indId) : next.add(indId);
    return next;
  });

  const toggleFamily = (famKey) => setExpandedFamilies((prev) => {
    const next = new Set(prev);
    next.has(famKey) ? next.delete(famKey) : next.add(famKey);
    return next;
  });

  // Build lookup maps
  const industryMap = Object.fromEntries(industries.map(i => [i.industry_id, i]));
  const familyMap = Object.fromEntries(families.map(f => [f.family_id, f]));

  // Origin-filtered view (does not affect the merge dialog, which always sees the full list).
  const displayRoles = originFilter === "all" ? roles : roles.filter((r) => originOf(r) === originFilter);

  // Group roles: industryId → familyId → roles[]
  const byIndustry = {};
  for (const role of displayRoles) {
    const indId = role.industry_id || "__none__";
    const famId = role.family_id || "__none__";
    if (!byIndustry[indId]) byIndustry[indId] = {};
    if (!byIndustry[indId][famId]) byIndustry[indId][famId] = [];
    byIndustry[indId][famId].push(role);
  }

  // Sort: named industries alphabetically, uncategorized last
  const sortedIndustryIds = Object.keys(byIndustry).sort((a, b) => {
    if (a === "__none__") return 1;
    if (b === "__none__") return -1;
    return (industryMap[a]?.name || "").localeCompare(industryMap[b]?.name || "");
  });

  const filteredFamilies = families.filter(f => f.industry_id === createIndustryId);

  const isSearchActive = searchQuery.trim().length > 0;
  const searchResults = isSearchActive
    ? displayRoles.filter((r) => {
        const q = searchQuery.toLowerCase();
        const indName = (industryMap[r.industry_id]?.name || "").toLowerCase();
        const famName = (familyMap[r.family_id]?.name || "").toLowerCase();
        return (
          r.name.toLowerCase().includes(q) ||
          (r.description || "").toLowerCase().includes(q) ||
          indName.includes(q) ||
          famName.includes(q)
        );
      })
    : [];

  // When an origin filter is active (and not searching), show a flat list for easy scanning
  // instead of the collapsible industry tree. Searching always shows a flat list too.
  const showFlatList = isSearchActive || originFilter !== "all";
  const flatList = isSearchActive ? searchResults : displayRoles;

  // Inline role row to avoid repetition
  const RoleRow = ({ role }) => {
    const roundGap = allGaps[role.role_id];
    const roundAlerts = allBankAlerts[role.role_id]?.rounds || {};
    return (
      <div className="grid grid-cols-12 border-r border-b border-border items-center hover:bg-secondary/20">
        <div className="col-span-4 p-4 border-r border-border min-w-0">
          <div className="flex items-center gap-3">
            <Switch checked={role.is_active} onCheckedChange={() => toggleActive(role)} />
            <div className="min-w-0">
              <div className="font-semibold truncate flex items-center gap-1.5">
                {role.name}
                {role.is_generating && (
                  <span className="inline-flex items-center gap-0.5 flex-shrink-0 font-mono text-[8px] uppercase tracking-wider px-1.5 py-0.5 bg-[#F59E0B]/10 text-[#B45309] rounded-lg border border-[#F59E0B]/30">
                    <Loader2 className="w-2.5 h-2.5 animate-spin" /> Generating
                  </span>
                )}
                {role.merged_into && (
                  <span className="flex-shrink-0 font-mono text-[8px] uppercase tracking-wider px-1.5 py-0.5 bg-secondary text-muted-foreground rounded-lg border border-border" title="Retired — merged into a unified role">
                    merged
                  </span>
                )}
                {role.source === "admin_merge" && (
                  <span className="flex-shrink-0 font-mono text-[8px] uppercase tracking-wider px-1.5 py-0.5 bg-[#1D4ED8]/10 text-[#1D4ED8] rounded-lg border border-[#1D4ED8]/30" title="Unified role created by merge">
                    unified
                  </span>
                )}
                {(() => {
                  const meta = ROLE_ORIGINS[originOf(role)];
                  return (
                    <span className={`flex-shrink-0 font-mono text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded-lg border ${meta.cls}`}
                      title={`Origin: ${meta.label}`}>
                      {meta.label}
                    </span>
                  );
                })()}
                {newRoleIds.has(role.role_id) && (
                  <span className="flex-shrink-0 font-mono text-[8px] uppercase tracking-wider px-1.5 py-0.5 bg-[#7C3AED] text-white rounded-lg" title="Created by a premium user since your last visit">
                    new
                  </span>
                )}
              </div>
              {role.description && <div className="text-xs text-muted-foreground truncate">{role.description}</div>}
              {originOf(role) === "premium" && (role.created_by_name || role.created_by_email) && (
                <div className="font-mono text-[10px] text-[#7C3AED] truncate" title={role.created_by_email || ""}>
                  by {role.created_by_name || "Unknown"}{role.created_by_email ? ` · ${role.created_by_email}` : ""}
                </div>
              )}
              <div className="font-mono text-[10px] text-muted-foreground">{role.role_id}</div>
            </div>
          </div>
        </div>

        {ROUNDS.map((rnd) => {
          const qCount = role[`${rnd}_question_count`] || 0;
          const bankVer = role[`${rnd}_bank_version`];
          const stale = qCount > 0 && (!bankVer || bankVer < CURRENT_BANK_PROMPT_VERSION);
          const gapInfo    = roundGap?.[rnd];
          const alertInfo  = roundAlerts[rnd];
          const topupInfo  = role.auto_topup_pending?.[rnd];
          // Auto-hide after 24h client-side
          const topupVisible = topupInfo && (Date.now() - new Date(topupInfo.triggered_at).getTime() < 86400000);
          const skillGapFills = ((role.skill_gap_topup?.[rnd]) || []).filter(
            f => (Date.now() - new Date(f.triggered_at).getTime()) < 86400000
          );
          const skillGapVisible = skillGapFills.length > 0;
          return (
            <div key={rnd} className="col-span-2 p-4 border-r border-border text-center">
              <div className="flex flex-col items-center gap-1">
                {qCount > 0 ? (
                  stale
                    ? <AlertTriangle className="w-4 h-4 text-[#F59E0B]" title="Bank may need regeneration" />
                    : <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                ) : (
                  <Circle className="w-4 h-4 text-muted-foreground" />
                )}
                <span className={`font-mono text-[10px] ${stale ? "text-[#F59E0B]" : "text-muted-foreground"}`}>
                  {qCount} Qs
                </span>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {role[`${rnd}_doc_count`] || 0} docs
                </span>
                {stale && <span className="font-mono text-[10px] text-[#F59E0B] font-bold">REGEN</span>}
                {gapInfo && (
                  <div className="mt-1 w-full">
                    <div className="flex items-center gap-1 justify-center">
                      <AlertTriangle className="w-3 h-3 text-[#F59E0B] flex-shrink-0" />
                      <span className="font-mono text-[9px] text-[#F59E0B] font-bold">
                        {gapInfo.gaps.length} GAP{gapInfo.gaps.length !== 1 ? "S" : ""}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-0.5 mt-0.5 justify-center">
                      {gapInfo.gaps.slice(0, 2).map((g) => (
                        <span key={g} className="font-mono text-[8px] px-1 py-0.5 bg-[#F59E0B]/10 text-[#F59E0B] rounded-lg border border-[#F59E0B]/30 truncate max-w-[80px]" title={g}>{g}</span>
                      ))}
                      {gapInfo.gaps.length > 2 && (
                        <span className="font-mono text-[8px] px-1 py-0.5 text-[#F59E0B]">+{gapInfo.gaps.length - 2}</span>
                      )}
                    </div>
                  </div>
                )}
                {alertInfo && (
                  <div className="mt-1 w-full">
                    <div className="flex items-center gap-1 justify-center">
                      <AlertTriangle className="w-3 h-3 text-[#E11D48] flex-shrink-0" />
                      <span className="font-mono text-[9px] text-[#E11D48] font-bold">
                        THIN BANK
                      </span>
                    </div>
                    <span className="font-mono text-[8px] text-[#E11D48]/80">
                      {alertInfo.affected_count} candidate{alertInfo.affected_count !== 1 ? "s" : ""} repeated
                    </span>
                  </div>
                )}
                {topupVisible && (
                  <div className="mt-1 w-full">
                    <div className="flex items-center gap-1 justify-center" title={`Auto-topped up on ${new Date(topupInfo.triggered_at).toLocaleDateString()}: ${topupInfo.questions_added} questions added`}>
                      <span className="font-mono text-[9px] text-[#D97706] font-bold">↑ AUTO-TOPPED</span>
                    </div>
                    <span className="font-mono text-[8px] text-[#D97706]/80">
                      +{topupInfo.questions_added} Qs
                    </span>
                  </div>
                )}
                {skillGapVisible && (
                  <div className="mt-1 w-full" title={skillGapFills.map(f => `${f.questions_added}Q for ${f.tool}`).join(", ")}>
                    <span className="font-mono text-[9px] text-[#6366F1] font-bold">⚡ SKILL-GAP</span>
                    <span className="font-mono text-[8px] text-[#6366F1]/80 block">
                      +{skillGapFills.reduce((s, f) => s + f.questions_added, 0)} Qs
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <div className="col-span-2 p-4 flex gap-2 flex-wrap items-center">
          <Button variant="outline" size="sm" className="rounded-xl h-8 px-2 text-xs"
            onClick={() => navigate(`/admin/roles/${role.role_id}`)}>
            <ChevronRight className="w-3 h-3 mr-1" /> Manage
          </Button>
          <Button variant="outline" size="sm" className="rounded-xl h-8 px-2 text-xs"
            onClick={() => {
              setEditTarget(role);
              setEditName(role.name);
              setEditDesc(role.description || "");
              setEditIndustryId(role.industry_id || "");
              setEditFamilyId(role.family_id || "");
            }}>
            <Pencil className="w-3 h-3" />
          </Button>
          <Button variant="outline" size="sm"
            className="rounded-xl h-8 px-2 text-xs hover:bg-[#E11D48] hover:text-white hover:border-[#E11D48]"
            onClick={() => setDeleteTarget(role)}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <main className="max-w-6xl mx-auto px-6 md:px-12 py-12">
      {/* Header */}
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <Link to="/admin" className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-[#0025D8] inline-flex items-center gap-1.5 transition-colors group">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-0.5 transition-transform" /> Admin
          </Link>
          <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mt-2">Interview Roles</h1>
          <p className="text-muted-foreground mt-1">Create roles with per-round reference docs and AI question banks.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/admin/industries">
            <Button variant="outline" className="rounded-xl h-12 px-5">
              <Building2 className="w-4 h-4 mr-2" /> Manage Industries
            </Button>
          </Link>
          <Button variant="outline" onClick={() => setMergeOpen(true)} className="rounded-xl h-12 px-5">
            <Merge className="w-4 h-4 mr-2" /> Merge Roles
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF] h-12 px-5">
            <Plus className="w-4 h-4 mr-2" /> New Role
          </Button>
        </div>
      </div>

      {/* Premium-user-created roles summary */}
      {!loading && premiumSummary && premiumSummary.total > 0 && (
        <div className="mb-6 rounded-xl border border-[#7C3AED]/30 bg-[#7C3AED]/5 p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-[#7C3AED]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Sparkles className="w-4 h-4 text-[#7C3AED]" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-sm text-[#0F172A]">
                  {premiumSummary.total} role{premiumSummary.total !== 1 ? "s" : ""} created by premium users
                  {premiumSummary.new_count > 0 && (
                    <span className="ml-2 font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#7C3AED] text-white rounded-lg">
                      {premiumSummary.new_count} new since last visit
                    </span>
                  )}
                </div>
                {premiumSummary.recent?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {premiumSummary.recent.map((r) => (
                      <li key={r.role_id} className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                        <button type="button" onClick={() => navigate(`/admin/roles/${r.role_id}`)}
                          className="font-medium text-foreground hover:text-[#7C3AED] transition-colors truncate max-w-[220px]">
                          {r.name}
                        </button>
                        {newRoleIds.has(r.role_id) && (
                          <span className="font-mono text-[8px] uppercase tracking-wider px-1 py-0.5 bg-[#7C3AED] text-white rounded">new</span>
                        )}
                        {(r.created_by_name || r.created_by_email) && (
                          <span className="font-mono text-[10px] text-[#7C3AED]/80 truncate">
                            by {r.created_by_name || "Unknown"}{r.created_by_email ? ` · ${r.created_by_email}` : ""}
                          </span>
                        )}
                        {r.created_at && (
                          <span className="font-mono text-[10px] opacity-60">
                            {new Date(r.created_at).toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <button type="button" onClick={() => setOriginFilter("premium")}
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#7C3AED]/40 text-[#7C3AED] text-xs font-semibold hover:bg-[#7C3AED]/10 transition-colors">
              View all premium roles <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Origin filter */}
      {!loading && roles.length > 0 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Origin</span>
          {ORIGIN_FILTERS.map(([val, label]) => {
            const count = val === "all" ? roles.length : roles.filter((r) => originOf(r) === val).length;
            return (
              <button key={val} type="button" onClick={() => setOriginFilter(val)}
                className={`px-3 py-1.5 rounded-xl border text-xs font-medium transition-colors ${
                  originFilter === val ? "border-[#1D4ED8] bg-[#1D4ED8]/8 text-[#1D4ED8]" : "border-border hover:bg-secondary"
                }`}>
                {label} <span className="opacity-60 font-mono">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Search bar */}
      {!loading && roles.length > 0 && (
        <div className="mb-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by role name, industry or job family…"
            className="w-full border border-input bg-background pl-9 pr-9 py-3 text-sm rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/30 focus:border-[#1D4ED8]"
          />
          {isSearchActive && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Role list */}
      {loading ? (
        <div className="p-12 font-mono text-sm text-muted-foreground">Loading roles…</div>
      ) : roles.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-muted-foreground">
          <p className="font-semibold">No roles yet.</p>
          <p className="text-sm mt-1">Create your first role to start building question banks.</p>
        </div>
      ) : (
        <div className="border-l border-t border-border">
          {/* Table header */}
          <div className="grid grid-cols-12 border-r border-b border-border bg-secondary/40 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <div className="col-span-4 p-3 border-r border-border">Role</div>
            <div className="col-span-2 p-3 border-r border-border text-center">HR</div>
            <div className="col-span-2 p-3 border-r border-border text-center">Technical</div>
            <div className="col-span-2 p-3 border-r border-border text-center">Behavioral</div>
            <div className="col-span-2 p-3">Actions</div>
          </div>

          {/* Flat list — when searching or filtering by origin */}
          {showFlatList ? (
            flatList.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm font-mono">
                {isSearchActive
                  ? <>No roles match <strong>"{searchQuery.trim()}"</strong>{originFilter !== "all" ? ` in ${ROLE_ORIGINS[originFilter].label}` : ""}</>
                  : <>No <strong>{ROLE_ORIGINS[originFilter].label}</strong> roles</>}
              </div>
            ) : (
              <>
                <div className="border-r border-b border-border px-4 py-2 font-mono text-[10px] text-muted-foreground bg-secondary/20">
                  {flatList.length} {isSearchActive ? `result${flatList.length !== 1 ? "s" : ""}` : `${ROLE_ORIGINS[originFilter].label} role${flatList.length !== 1 ? "s" : ""}`}
                </div>
                {flatList.map((role) => <RoleRow key={role.role_id} role={role} />)}
              </>
            )
          ) : (
          /* Grouped rows: industry → family → roles (collapsible) */
          sortedIndustryIds.map((indId) => {
            const indExpanded = expandedIndustries.has(indId);
            const totalRoles = Object.values(byIndustry[indId]).flat().length;
            const sortedFamilies = Object.entries(byIndustry[indId]).sort(([a], [b]) => {
              if (a === "__none__") return 1;
              if (b === "__none__") return -1;
              return (familyMap[a]?.name || "").localeCompare(familyMap[b]?.name || "");
            });

            return (
              <React.Fragment key={indId}>
                {/* Industry header — full row clickable */}
                <button
                  onClick={() => toggleIndustry(indId)}
                  className={`col-span-12 w-full border-r border-b border-border px-4 py-2.5 flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] transition-colors ${
                    indId === "__none__"
                      ? "bg-secondary/60 text-muted-foreground hover:bg-secondary/80"
                      : "bg-[#1D4ED8]/10 text-[#1D4ED8] hover:bg-[#1D4ED8]/15"
                  }`}
                >
                  <ChevronRight
                    className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${indExpanded ? "rotate-90" : ""}`}
                  />
                  <Building2 className="w-3 h-3 flex-shrink-0" />
                  {indId === "__none__" ? "Uncategorized" : (industryMap[indId]?.name || indId)}
                  <span className="ml-1 font-normal normal-case opacity-60">
                    · {totalRoles} role{totalRoles !== 1 ? "s" : ""}
                    {!indExpanded && ` · ${sortedFamilies.filter(([f]) => f !== "__none__").length} famil${sortedFamilies.filter(([f]) => f !== "__none__").length !== 1 ? "ies" : "y"}`}
                  </span>
                </button>

                {/* Families — only visible when industry is expanded */}
                {indExpanded && sortedFamilies.map(([famId, famRoles]) => {
                  const famKey = `${indId}::${famId}`;
                  const famExpanded = expandedFamilies.has(famKey);

                  return (
                    <React.Fragment key={famId}>
                      {famId === "__none__" ? (
                        /* Roles with no family — show directly under the industry */
                        famRoles.map((role) => <RoleRow key={role.role_id} role={role} />)
                      ) : (
                        <>
                          {/* Family header — full row clickable */}
                          <button
                            onClick={() => toggleFamily(famKey)}
                            className="col-span-12 w-full border-r border-b border-border px-6 py-1.5 flex items-center gap-2 font-mono text-[9px] text-muted-foreground uppercase tracking-[0.25em] bg-secondary/20 border-l-2 border-l-[#1D4ED8]/30 hover:bg-secondary/40 transition-colors"
                          >
                            <ChevronRight
                              className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 ${famExpanded ? "rotate-90" : ""}`}
                            />
                            {familyMap[famId]?.name || famId}
                            <span className="font-normal normal-case opacity-60 ml-1">
                              · {famRoles.length} role{famRoles.length !== 1 ? "s" : ""}
                            </span>
                          </button>

                          {/* Role rows — only visible when family is expanded */}
                          {famExpanded && famRoles.map((role) => (
                            <RoleRow key={role.role_id} role={role} />
                          ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })
          )}
        </div>
      )}

      {/* Create Dialog — 2-step wizard */}
      <Dialog open={createOpen} onOpenChange={(o) => { if (!o) resetCreate(); }}>
        <DialogContent className="rounded-xl max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <DialogTitle className="font-display font-bold">New interview role</DialogTitle>
              <div className="flex items-center gap-1 ml-auto">
                {[1, 2].map(s => (
                  <div key={s} className={`w-6 h-1.5 rounded-full transition-colors ${s <= createStep ? "bg-[#1D4ED8]" : "bg-border"}`} />
                ))}
              </div>
            </div>
            <DialogDescription className="font-mono text-[10px] uppercase tracking-widest">
              {createStep === 1 ? "Step 1 · Role details" : "Step 2 · Experience & tools"}
            </DialogDescription>
          </DialogHeader>

          {createStep === 1 && (
            <div className="space-y-4">
              {/* Name */}
              <div ref={nameWrapperRef} className="relative">
                <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Role name</Label>
                <div className="relative">
                  <Input autoFocus value={createName}
                    onChange={(e) => { setCreateName(e.target.value); fetchNameSuggestions(e.target.value); }}
                    onKeyDown={(e) => { if (e.key === "Escape") setShowNameSuggestions(false); if (e.key === "Enter" && createName.trim()) performCreate(); }}
                    onFocus={() => createName.trim().length >= 2 && nameSuggestions.length > 0 && setShowNameSuggestions(true)}
                    placeholder="e.g. Frontend Engineer" className="rounded-xl py-6 pr-8" autoComplete="off" />
                  {nameLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground pointer-events-none" />}
                </div>
                {showNameSuggestions && nameSuggestions.length > 0 && (
                  <ul className="absolute z-50 top-full left-0 right-0 mt-0.5 bg-white rounded-xl border border-border shadow-lg max-h-52 overflow-y-auto">
                    {nameSuggestions.map((r) => (
                      <li key={r.name}>
                        <button type="button"
                          onMouseDown={(e) => { e.preventDefault(); setCreateName(r.name); setNameSuggestions([]); setShowNameSuggestions(false); }}
                          className="w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 text-sm hover:bg-[#1D4ED8]/5 hover:text-[#1D4ED8] transition-colors border-b border-border/40 last:border-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <Briefcase className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="truncate">{r.name}</span>
                          </div>
                          {r.source === "internal" && (
                            <span className="flex-shrink-0 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#1D4ED8]/10 text-[#1D4ED8] rounded-lg border border-[#1D4ED8]/20">exists</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {/* Description */}
              <div>
                <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">
                  Description <span className="normal-case font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Input value={createDesc} onChange={(e) => setCreateDesc(e.target.value)}
                  placeholder="Shown to candidates when selecting a role" className="rounded-xl py-6" />
              </div>
              {/* Industry */}
              <div>
                <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">
                  Industry <span className="normal-case font-normal text-muted-foreground">(optional)</span>
                </Label>
                <select value={createIndustryId}
                  onChange={(e) => { setCreateIndustryId(e.target.value); setCreateFamilyId(""); }}
                  className="w-full border border-input bg-background px-3 h-12 text-sm font-mono rounded-xl">
                  <option value="">— No industry</option>
                  {industries.map((ind) => <option key={ind.industry_id} value={ind.industry_id}>{ind.name}</option>)}
                </select>
              </div>
              {/* Family */}
              <div>
                <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">
                  Job Family <span className="normal-case font-normal text-muted-foreground">(optional)</span>
                </Label>
                <select value={createFamilyId} onChange={(e) => setCreateFamilyId(e.target.value)}
                  disabled={!createIndustryId || filteredFamilies.length === 0}
                  className="w-full border border-input bg-background px-3 h-12 text-sm font-mono rounded-xl disabled:opacity-50 disabled:cursor-not-allowed">
                  <option value="">— No job family</option>
                  {filteredFamilies.map((f) => <option key={f.family_id} value={f.family_id}>{f.name}</option>)}
                </select>
              </div>
            </div>
          )}

          {createStep === 2 && (
            <div className="space-y-5">
              {/* Tools — auto-discovered from the role; admin can edit */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="uppercase tracking-wider text-xs font-semibold block">
                    Key tools & technologies
                  </Label>
                  {toolsDiscovering ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-[#1D4ED8]">
                      <Loader2 className="w-3 h-3 animate-spin" /> Finding tools…
                    </span>
                  ) : (
                    <button type="button" onClick={() => discoverTools({ force: true })}
                      disabled={!createName.trim()}
                      className="inline-flex items-center gap-1.5 text-[11px] font-mono text-[#1D4ED8] hover:text-[#1E40AF] disabled:opacity-40 transition-colors">
                      <Sparkles className="w-3 h-3" /> Re-suggest
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground font-mono mb-2">Auto-detected from the role — remove any that don't fit or add your own.</p>
                {/* Chips */}
                {createTools.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {createTools.map(t => (
                      <span key={t} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-[#1D4ED8]/30 bg-[#1D4ED8]/8 text-[#1D4ED8] font-mono text-xs">
                        {t}
                        <button type="button" onClick={() => removeTool(t)} className="hover:text-[#E11D48] transition-colors"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
                {/* Input */}
                <div className="relative">
                  <Input ref={toolInputRef} value={toolInput}
                    onChange={(e) => { setToolInput(e.target.value); fetchToolSuggestions(e.target.value); }}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" || e.key === ",") && toolInput.trim()) { e.preventDefault(); addTool(toolInput); }
                      if (e.key === "Backspace" && !toolInput && createTools.length) removeTool(createTools[createTools.length - 1]);
                    }}
                    placeholder="Type a tool and press Enter… (e.g. React, PostgreSQL)"
                    className="rounded-xl" />
                  {toolSuggestions.length > 0 && (
                    <ul className="absolute z-50 top-full left-0 right-0 mt-0.5 bg-white rounded-xl border border-border shadow-lg max-h-40 overflow-y-auto">
                      {toolSuggestions.map(t => (
                        <li key={t}>
                          <button type="button" onMouseDown={(e) => { e.preventDefault(); addTool(t); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-[#1D4ED8]/5 hover:text-[#1D4ED8] transition-colors border-b border-border/40 last:border-0 font-mono">
                            {t}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Auto-generate toggle */}
              <div className={`rounded-xl border p-4 transition-colors ${createAutoGenerate ? "border-[#10B981]/40 bg-[#10B981]/5" : "border-border bg-secondary/30"}`}>
                <div className="flex items-start gap-3">
                  <Switch checked={createAutoGenerate} onCheckedChange={setCreateAutoGenerate} className="mt-0.5" />
                  <div>
                    <div className="flex items-center gap-1.5 font-semibold text-sm">
                      <Wand2 className="w-3.5 h-3.5" />
                      Auto-generate reference doc &amp; questions
                    </div>
                    <p className="text-[11px] text-muted-foreground font-mono mt-0.5">
                      {createAutoGenerate
                        ? "Claude writes a reference doc, then populates all 3 rounds in the background (~60s) — 50 questions each, spanning junior→senior via Easy/Medium/Hard difficulty (15/20/15)."
                        : "Role is created empty — you'll add docs and generate questions manually from the detail page."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {createStep === 2
              ? <Button variant="outline" className="rounded-xl" onClick={() => setCreateStep(1)} disabled={busy}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
              : <Button variant="outline" className="rounded-xl" onClick={resetCreate}>Cancel</Button>
            }
            <Button onClick={performCreate} disabled={busy || !createName.trim()}
              className="rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF]">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> :
               createStep === 1 ? <>Next <ChevronRight className="w-4 h-4 ml-1" /></> :
               createAutoGenerate ? <><Wand2 className="w-4 h-4 mr-2" /> Create &amp; Generate</> :
               <><Plus className="w-4 h-4 mr-2" /> Create role</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) setEditTarget(null); }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold">Edit role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Role name</Label>
              <Input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} className="rounded-xl py-6" />
            </div>
            <div>
              <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Description</Label>
              <Input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} className="rounded-xl py-6" />
            </div>
            <div>
              <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Industry</Label>
              <select
                value={editIndustryId}
                onChange={(e) => { setEditIndustryId(e.target.value); setEditFamilyId(""); }}
                className="w-full border border-input bg-background px-3 h-12 text-sm font-mono rounded-xl"
              >
                <option value="">— No industry (uncategorized)</option>
                {industries.map((ind) => (
                  <option key={ind.industry_id} value={ind.industry_id}>{ind.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Job Family</Label>
              <select
                value={editFamilyId}
                onChange={(e) => setEditFamilyId(e.target.value)}
                disabled={!editIndustryId || families.filter(f => f.industry_id === editIndustryId).length === 0}
                className="w-full border border-input bg-background px-3 h-12 text-sm font-mono rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">— No job family</option>
                {families.filter(f => f.industry_id === editIndustryId).map((f) => (
                  <option key={f.family_id} value={f.family_id}>{f.name}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={performEdit} disabled={busy || !editName.trim()}
              className="rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF]">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <MergeRolesDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        roles={roles}
        industries={industries}
        families={families}
        onMerged={(newId) => { fetchRoles(); navigate(`/admin/roles/${newId}`); }}
      />

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-[#E11D48]">Delete role</DialogTitle>
            <DialogDescription>
              Delete <strong>{deleteTarget?.name}</strong>? This removes all reference documents and the question bank permanently. Existing interviews are not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={performDelete} disabled={busy} className="rounded-xl bg-[#E11D48] hover:bg-[#BE123C]">
              <Trash2 className="w-4 h-4 mr-2" /> {busy ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
